import React, { useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import HomeHeader from '../home-page/components/HomeHeader';
import Icon from '../../components/AppIcon';
import NavigationBar from '../../components/ui/NavigationBar';
import CategoryFilter from './components/CategoryFilter';
import SearchAndSort from './components/SearchAndSort';
import TemplateCard from './components/TemplateCard';
import FeaturedTemplates from './components/FeaturedTemplates';
import EmptyState from './components/EmptyState';
import LoadingStateManager from '../../components/ui/LoadingStateManager';
import Button from '../../components/ui/Button';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { useAuth } from '../../contexts/AuthContext';
import { formatLimit } from '../../config/planFeatures';
import { getTemplatePageById, getTemplatePageBySlug, getTemplatePageMetadata } from '../../config/templatePages.mjs';
import FormPreviewRenderer from './components/FormPreviewRenderer';

// App theme header element added to every template as a starting point
const APP_THEME_HEADER = {
  id: 'image',
  name: 'Form Header',
  type: 'media',
  label: 'Form Header',
  src: '/assets/images/web-forms-logo-1769800970615.png',
  alt: 'WebForms logo — professional form builder platform',
  content: 'Form Header Image'
};

const UpgradeLimitModal = ({ open, onClose, onUpgrade, limits, planType }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl shadow-elevation-lg w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
              <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground">Form Limit Reached</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-smooth" aria-label="Close">
            <Icon name="X" size={20} />
          </button>
        </div>
        <div className="bg-error/5 border border-error/20 rounded-lg p-4 mb-5">
          <p className="text-sm text-foreground leading-relaxed">
            You've used all <strong>{formatLimit(limits?.maxForms)} form{limits?.maxForms === 1 ? '' : 's'}</strong>{' '}
            on your <strong className="capitalize">{planType}</strong> plan.
          </p>
        </div>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Delete an existing form or upgrade your account to create another one.
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <Button variant="default" className="w-full sm:w-auto" iconName="Zap" iconPosition="left" onClick={onUpgrade}>
            Upgrade Plan
          </Button>
          <Button variant="outline" className="w-full sm:w-auto" onClick={onClose}>Maybe Later</Button>
        </div>
      </div>
    </div>
  );
};

const TemplatesGallery = () => {
  const navigate = useNavigate();
  const { templateSlug } = useParams();
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [isLoading, setIsLoading] = useState(false);
  const [showUpgradeLimitModal, setShowUpgradeLimitModal] = useState(false);
  const { canCreateForm, limits, planType: creatorPlanType } = usePlanLimits();
  const { user } = useAuth();

  const categories = [
  { id: 'all', label: 'All Templates', icon: 'LayoutGrid', count: 24 },
  { id: 'contact', label: 'Contact Forms', icon: 'Mail', count: 6 },
  { id: 'lead', label: 'Lead Generation', icon: 'Target', count: 5 },
  { id: 'survey', label: 'Surveys', icon: 'ClipboardList', count: 4 },
  { id: 'booking', label: 'Booking', icon: 'Calendar', count: 3 },
  { id: 'payment', label: 'Payment', icon: 'CreditCard', count: 3 },
  { id: 'registration', label: 'Registration', icon: 'UserPlus', count: 3 }];


  const allTemplates = [
  // ─── 1. Contact Us Form ───────────────────────────────────────────────────
  {
    id: 1,
    title: 'Contact Us Form',
    description: 'Simple contact form with name, email, phone, and message fields. Perfect for basic customer inquiries.',
    category: 'Contact Forms',
    categoryIcon: 'Mail',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_11afbf205-1768345350771.png",
    thumbnailAlt: 'Modern contact form interface with clean white background showing name email phone and message input fields arranged vertically',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_10512da67-1771889094771.png",
    previewImageAlt: 'Detailed view of contact form template with professional layout showing all form fields labels and submit button',
    usageCount: '2.4k',
    fieldCount: 5,
    sectionCount: 1,
    hasConditionalLogic: false,
    isFeatured: true,
    isNew: false,
    fullDescription: 'A clean and professional contact form template designed for businesses to collect customer inquiries. Includes validation for all fields and customizable styling options.',
    includedElements: ['Text Input', 'Email Input', 'Phone Input', 'Textarea', 'Submit Button'],
    features: [
    'Mobile-responsive design with optimized touch targets',
    'Built-in email validation and phone number formatting',
    'Customizable field labels and placeholder text',
    'Success message display after form submission',
    'Optional consent checkbox to support GDPR workflows'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'Enter your full name', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'your.email@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: false },
    { id: 'text', name: 'Subject', type: 'input', label: 'Subject', placeholder: 'What is this regarding?', required: true },
    { id: 'text', name: 'Message', type: 'input', label: 'Message', placeholder: 'Tell us how we can help you...', required: true, multiline: true }]

  },
  // ─── 2. Lead Capture Form ────────────────────────────────────────────────
  {
    id: 2,
    title: 'Lead Capture Form',
    description: 'Multi-step lead generation form with company details, budget range, and project timeline.',
    category: 'Lead Generation',
    categoryIcon: 'Target',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1417d1ee5-1771012096550.png",
    thumbnailAlt: 'Professional lead generation form with multiple steps showing progress indicator and business information fields on gradient blue background',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_192700b0d-1768798964211.png",
    previewImageAlt: 'Complete lead capture form template displaying all steps including company details budget selection and timeline picker',
    usageCount: '1.8k',
    fieldCount: 12,
    sectionCount: 3,
    hasConditionalLogic: true,
    isFeatured: true,
    isNew: false,
    fullDescription: 'A comprehensive lead generation form with multi-step progression to increase conversion rates. Collects detailed business information while maintaining user engagement.',
    includedElements: ['Text Input', 'Email Input', 'Dropdown', 'Radio Buttons', 'Date Picker', 'Progress Bar'],
    features: [
    'Multi-step wizard with progress tracking and navigation',
    'Conditional logic to show relevant fields based on selections',
    'Budget range selector with custom options',
    'Project timeline picker with calendar integration',
    'Lead scoring integration for sales team prioritization'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Contact Information', type: 'content', label: 'Contact Information', content: 'Contact Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Work Email', placeholder: 'john@company.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'heading', name: 'Company Details', type: 'content', label: 'Company Details', content: 'Company Details' },
    { id: 'text', name: 'Company Name', type: 'input', label: 'Company Name', placeholder: 'Acme Corporation', required: true },
    { id: 'dropdown', name: 'Company Size', type: 'select', label: 'Company Size', placeholder: 'Select company size', required: true, options: ['1-10 employees', '11-50 employees', '51-200 employees', '201-500 employees', '500+ employees'] },
    { id: 'dropdown', name: 'Industry', type: 'select', label: 'Industry', placeholder: 'Select your industry', required: true, options: ['Technology', 'Healthcare', 'Finance', 'Retail', 'Manufacturing', 'Education', 'Other'] },
    { id: 'heading', name: 'Project Information', type: 'content', label: 'Project Information', content: 'Project Information' },
    { id: 'radio', name: 'Budget Range', type: 'radio', label: 'Budget Range', required: true, options: ['Less than $10,000', '$10,000 - $50,000', '$50,000 - $100,000', '$100,000+'] },
    { id: 'date', name: 'Timeline', type: 'input', label: 'Desired Start Date', placeholder: 'Select date', required: true },
    { id: 'text', name: 'Project Description', type: 'input', label: 'Project Description', placeholder: 'Tell us about your project...', required: true, multiline: true }]

  },
  // ─── 3. Customer Feedback Survey ─────────────────────────────────────────
  {
    id: 3,
    title: 'Customer Feedback Survey',
    description: 'Comprehensive survey with rating scales, multiple choice, and open-ended questions.',
    category: 'Surveys',
    categoryIcon: 'ClipboardList',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_11d1bcb9a-1769696343683.png",
    thumbnailAlt: 'Customer satisfaction survey form with star rating system multiple choice questions and text feedback area on light background',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1867e003c-1774285020929.png",
    previewImageAlt: 'Full customer feedback survey template showing rating scales checkbox options and comment boxes with modern design',
    usageCount: '1.5k',
    fieldCount: 15,
    sectionCount: 4,
    hasConditionalLogic: true,
    isFeatured: true,
    isNew: true,
    fullDescription: 'A detailed customer feedback survey template designed to gather comprehensive insights about customer satisfaction and experience.',
    includedElements: ['Rating Scale', 'Checkboxes', 'Radio Buttons', 'Textarea', 'Dropdown', 'Slider'],
    features: [
    'Multiple question types including NPS and CSAT ratings',
    'Skip logic to personalize survey based on responses',
    'Anonymous submission option for honest feedback',
    'Progress indicator to show completion status',
    'Export results to CSV for analysis and reporting'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Overall Satisfaction', type: 'content', label: 'Overall Satisfaction', content: 'Overall Satisfaction' },
    { id: 'radio', name: 'Overall Rating', type: 'radio', label: 'How satisfied are you with our product/service?', required: true, options: ['Very Satisfied', 'Satisfied', 'Neutral', 'Dissatisfied', 'Very Dissatisfied'] },
    { id: 'slider', name: 'NPS Score', type: 'slider', label: 'How likely are you to recommend us to a friend? (0-10)', required: true, min: 0, max: 10 },
    { id: 'heading', name: 'Product Experience', type: 'content', label: 'Product Experience', content: 'Product Experience' },
    { id: 'checkbox', name: 'Features Used', type: 'checkbox', label: 'Which features have you used?', required: false, options: ['Dashboard', 'Reports', 'Integrations', 'Mobile App', 'API Access', 'Team Collaboration'] },
    { id: 'radio', name: 'Ease of Use', type: 'radio', label: 'How easy is our product to use?', required: true, options: ['Very Easy', 'Easy', 'Moderate', 'Difficult', 'Very Difficult'] },
    { id: 'text', name: 'Favorite Feature', type: 'input', label: 'What is your favorite feature?', placeholder: 'Tell us what you love...', required: false, multiline: true },
    { id: 'heading', name: 'Support & Service', type: 'content', label: 'Support & Service', content: 'Support & Service' },
    { id: 'radio', name: 'Support Quality', type: 'radio', label: 'How would you rate our customer support?', required: false, options: ['Excellent', 'Good', 'Average', 'Poor', "Haven\'t Used"] },
    { id: 'dropdown', name: 'Response Time', type: 'select', label: 'How quickly did we respond to your inquiries?', placeholder: 'Select response time', required: false, options: ['Within 1 hour', '1-4 hours', '4-24 hours', 'More than 24 hours', 'No response yet'] },
    { id: 'heading', name: 'Improvements', type: 'content', label: 'Improvements', content: 'Help Us Improve' },
    { id: 'text', name: 'Improvements', type: 'input', label: 'What could we improve?', placeholder: 'Share your suggestions...', required: false, multiline: true },
    { id: 'checkbox', name: 'Missing Features', type: 'checkbox', label: 'What features would you like to see?', required: false, options: ['Advanced Analytics', 'More Integrations', 'Custom Branding', 'Better Mobile Experience', 'Other'] },
    { id: 'text', name: 'Additional Comments', type: 'input', label: 'Any additional comments?', placeholder: "Anything else you'd like to share...", required: false, multiline: true }]

  },
  // ─── 4. Appointment Booking ───────────────────────────────────────────────
  {
    id: 4,
    title: 'Appointment Booking',
    description: 'Service booking form with date/time selection, service type, and customer information.',
    category: 'Booking',
    categoryIcon: 'Calendar',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1cf28c2b2-1769336279196.png",
    thumbnailAlt: 'Appointment booking interface with calendar date picker time slot selection and service options displayed in organized layout',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1c333c3c2-1766836805431.png",
    previewImageAlt: 'Complete appointment booking form showing calendar view available time slots service selection and customer details section',
    usageCount: '1.2k',
    fieldCount: 10,
    sectionCount: 3,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: true,
    fullDescription: 'A professional appointment request form that collects preferred services, dates, times, and customer contact details.',
    includedElements: ['Date Picker', 'Time Picker', 'Dropdown', 'Text Input', 'Email Input', 'Phone Input'],
    features: [
    'Preferred date and time fields for scheduling requests',
    'Service duration and pricing details can be added',
    'Contact fields for booking follow-up',
    'Customizable service choices and instructions',
    'Optional notes for appointment requirements'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Service Selection', type: 'content', label: 'Service Selection', content: 'Select Your Service' },
    { id: 'dropdown', name: 'Service Type', type: 'select', label: 'Service Type', placeholder: 'Choose a service', required: true, options: ['Consultation (30 min)', 'Standard Service (1 hour)', 'Premium Service (2 hours)', 'Full Day Session'] },
    { id: 'date', name: 'Preferred Date', type: 'input', label: 'Preferred Date', placeholder: 'Select date', required: true },
    { id: 'time', name: 'Preferred Time', type: 'input', label: 'Preferred Time', placeholder: 'Select time', required: true },
    { id: 'heading', name: 'Your Information', type: 'content', label: 'Your Information', content: 'Your Contact Details' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'heading', name: 'Additional Details', type: 'content', label: 'Additional Details', content: 'Additional Information' },
    { id: 'text', name: 'Special Requests', type: 'input', label: 'Special Requests or Notes', placeholder: 'Any specific requirements or questions?', required: false, multiline: true }]

  },
  // ─── 5. Payment Collection Form ──────────────────────────────────────────
  {
    id: 5,
    title: 'Payment Collection Form',
    description: 'Payment form layout with amount, currency, billing details, and customer contact fields.',
    category: 'Payment',
    categoryIcon: 'CreditCard',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1049619ac-1764759999684.png",
    thumbnailAlt: 'Secure payment form interface with credit card input fields billing address section and payment amount display on professional background',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_161d121fa-1772231539772.png",
    previewImageAlt: 'Complete payment collection form showing card details billing information security badges and payment summary section',
    usageCount: '980',
    fieldCount: 14,
    sectionCount: 3,
    hasConditionalLogic: false,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A customizable payment collection form layout for amount, currency, billing information, and payment-related details. Review your connected payment provider and account setup before publishing.',
    includedElements: ['Card Input', 'Text Input', 'Dropdown', 'Checkbox', 'Amount Display'],
    features: [
    'Payment-provider connection can be configured in the builder',
    'Currency choices can be tailored to the business',
    'Billing and customer contact information fields',
    'Payment purpose and amount collection',
    'Terms acknowledgment option before submission'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Payment Details', type: 'content', label: 'Payment Details', content: 'Payment Information' },
    { id: 'number', name: 'Amount', type: 'input', label: 'Payment Amount', placeholder: '0.00', required: true },
    { id: 'dropdown', name: 'Currency', type: 'select', label: 'Currency', placeholder: 'Select currency', required: true, options: ['USD - US Dollar', 'EUR - Euro', 'GBP - British Pound', 'CAD - Canadian Dollar'] },
    { id: 'text', name: 'Description', type: 'input', label: 'Payment Description', placeholder: 'What is this payment for?', required: true },
    { id: 'heading', name: 'Billing Information', type: 'content', label: 'Billing Information', content: 'Billing Address' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'Name on card', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'your.email@example.com', required: true },
    { id: 'text', name: 'Address Line 1', type: 'input', label: 'Address Line 1', placeholder: 'Street address', required: true },
    { id: 'text', name: 'Address Line 2', type: 'input', label: 'Address Line 2', placeholder: 'Apt, suite, etc. (optional)', required: false },
    { id: 'text', name: 'City', type: 'input', label: 'City', placeholder: 'City', required: true },
    { id: 'text', name: 'State', type: 'input', label: 'State/Province', placeholder: 'State or province', required: true },
    { id: 'text', name: 'Postal Code', type: 'input', label: 'Postal Code', placeholder: 'ZIP or postal code', required: true },
    { id: 'dropdown', name: 'Country', type: 'select', label: 'Country', placeholder: 'Select country', required: true, options: ['United States', 'Canada', 'United Kingdom', 'Australia', 'Germany', 'France', 'Other'] },
    { id: 'checkbox', name: 'Terms', type: 'checkbox', label: 'I agree to the terms and conditions', required: true, options: ['I agree to the terms and conditions'] }]

  },
  // ─── 6. Event Registration ────────────────────────────────────────────────
  {
    id: 6,
    title: 'Event Registration',
    description: 'Multi-attendee registration form with ticket selection, dietary preferences, and payment.',
    category: 'Registration',
    categoryIcon: 'UserPlus',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1501e9c49-1773040427742.png",
    thumbnailAlt: 'Event registration form with attendee information fields ticket type selection and dietary preference options on modern interface',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1da58fe02-1770062248211.png',
    previewImageAlt: 'Full event registration template showing multiple attendee forms ticket pricing dietary options and payment integration',
    usageCount: '850',
    fieldCount: 18,
    sectionCount: 4,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A comprehensive event registration form that handles multiple attendees, ticket types, and special requirements.',
    includedElements: ['Text Input', 'Email Input', 'Dropdown', 'Checkboxes', 'Radio Buttons', 'File Upload'],
    features: [
    'Multiple attendee registration in single submission',
    'Dynamic ticket pricing with early bird discounts',
    'Dietary restrictions and accessibility requirements',
    'QR code generation for event check-in',
    'Automated confirmation emails with event details'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Event Details', type: 'content', label: 'Event Details', content: 'Event Registration' },
    { id: 'dropdown', name: 'Ticket Type', type: 'select', label: 'Ticket Type', placeholder: 'Select ticket type', required: true, options: ['General Admission - $50', 'VIP Pass - $150', 'Student Ticket - $25', 'Group Package (5+) - $200'] },
    { id: 'number', name: 'Number of Attendees', type: 'input', label: 'Number of Attendees', placeholder: '1', required: true },
    { id: 'heading', name: 'Primary Attendee', type: 'content', label: 'Primary Attendee', content: 'Primary Attendee Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'text', name: 'Company', type: 'input', label: 'Company/Organization', placeholder: 'Company name (optional)', required: false },
    { id: 'heading', name: 'Preferences', type: 'content', label: 'Preferences', content: 'Dietary & Accessibility' },
    { id: 'checkbox', name: 'Dietary Restrictions', type: 'checkbox', label: 'Dietary Restrictions', required: false, options: ['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Nut Allergy', 'Other'] },
    { id: 'checkbox', name: 'Accessibility', type: 'checkbox', label: 'Accessibility Requirements', required: false, options: ['Wheelchair Access', 'Sign Language Interpreter', 'Hearing Assistance', 'Visual Assistance', 'Other'] },
    { id: 'heading', name: 'Additional Information', type: 'content', label: 'Additional Information', content: 'Workshop & Sessions' },
    { id: 'checkbox', name: 'Workshop Interest', type: 'checkbox', label: 'Which workshops are you interested in?', required: false, options: ['Morning Workshop A', 'Morning Workshop B', 'Afternoon Workshop C', 'Afternoon Workshop D'] },
    { id: 'radio', name: 'T-Shirt Size', type: 'radio', label: 'T-Shirt Size', required: false, options: ['Small', 'Medium', 'Large', 'X-Large', 'XX-Large'] },
    { id: 'text', name: 'Special Requests', type: 'input', label: 'Special Requests', placeholder: 'Any special requests or questions?', required: false, multiline: true },
    { id: 'checkbox', name: 'Marketing Consent', type: 'checkbox', label: 'Communication Preferences', required: false, options: ['Send me updates about future events', 'Add me to the mailing list'] }]

  },
  // ─── 7. Job Application Form ──────────────────────────────────────────────
  {
    id: 7,
    title: 'Job Application Form',
    description: 'Comprehensive job application with resume upload, work history, and reference details.',
    category: 'Registration',
    categoryIcon: 'UserPlus',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_188eaa224-1785433575611.png",
    thumbnailAlt: 'Job application form interface with personal information section resume upload area work experience fields and reference details',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_19b076721-1765268301748.png",
    previewImageAlt: 'Complete job application template displaying all sections including education work history skills assessment and document uploads',
    usageCount: '720',
    fieldCount: 22,
    sectionCount: 5,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A detailed job application form designed to collect comprehensive candidate information for recruitment processes.',
    includedElements: ['Text Input', 'Email Input', 'Phone Input', 'File Upload', 'Textarea', 'Date Picker', 'Dropdown'],
    features: [
    'Resume and cover letter upload with file validation',
    'Work history with multiple position entries',
    'Education background and degree information fields',
    'Professional references with contact information',
    'Skills assessment with proficiency ratings'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Personal Information', type: 'content', label: 'Personal Information', content: 'Personal Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'text', name: 'Address', type: 'input', label: 'Current Address', placeholder: 'Street, City, State, ZIP', required: true },
    { id: 'url', name: 'LinkedIn', type: 'input', label: 'LinkedIn Profile', placeholder: 'https://linkedin.com/in/yourprofile', required: false },
    { id: 'heading', name: 'Position Details', type: 'content', label: 'Position Details', content: 'Position Applied For' },
    { id: 'dropdown', name: 'Position', type: 'select', label: 'Position', placeholder: 'Select position', required: true, options: ['Software Engineer', 'Product Manager', 'UX Designer', 'Marketing Manager', 'Sales Representative', 'Customer Support', 'Other'] },
    { id: 'dropdown', name: 'Employment Type', type: 'select', label: 'Desired Employment Type', placeholder: 'Select type', required: true, options: ['Full-time', 'Part-time', 'Contract', 'Internship'] },
    { id: 'date', name: 'Available Start Date', type: 'input', label: 'Available Start Date', placeholder: 'Select date', required: true },
    { id: 'number', name: 'Salary Expectation', type: 'input', label: 'Salary Expectation (Annual)', placeholder: '0', required: false },
    { id: 'heading', name: 'Education', type: 'content', label: 'Education', content: 'Educational Background' },
    { id: 'dropdown', name: 'Highest Degree', type: 'select', label: 'Highest Degree', placeholder: 'Select degree', required: true, options: ["High School", "Associate Degree", "Bachelor\'s Degree", "Master\'s Degree", 'Doctorate', 'Other'] },
    { id: 'text', name: 'School Name', type: 'input', label: 'School/University Name', placeholder: 'University name', required: true },
    { id: 'text', name: 'Field of Study', type: 'input', label: 'Field of Study', placeholder: 'Major or specialization', required: true },
    { id: 'date', name: 'Graduation Date', type: 'input', label: 'Graduation Date', placeholder: 'Select date', required: true },
    { id: 'heading', name: 'Work Experience', type: 'content', label: 'Work Experience', content: 'Work Experience' },
    { id: 'text', name: 'Current Employer', type: 'input', label: 'Current/Most Recent Employer', placeholder: 'Company name', required: true },
    { id: 'text', name: 'Job Title', type: 'input', label: 'Job Title', placeholder: 'Your position', required: true },
    { id: 'text', name: 'Years of Experience', type: 'input', label: 'Total Years of Experience', placeholder: 'Years in this field', required: true },
    { id: 'text', name: 'Key Responsibilities', type: 'input', label: 'Key Responsibilities', placeholder: 'Describe your main responsibilities...', required: true, multiline: true },
    { id: 'heading', name: 'Documents', type: 'content', label: 'Documents', content: 'Required Documents' },
    { id: 'file', name: 'Resume', type: 'file', label: 'Resume/CV', placeholder: 'Upload your resume', required: true },
    { id: 'file', name: 'Cover Letter', type: 'file', label: 'Cover Letter', placeholder: 'Upload cover letter (optional)', required: false },
    { id: 'heading', name: 'Additional Information', type: 'content', label: 'Additional Information', content: 'Additional Information' },
    { id: 'text', name: 'Why Join', type: 'input', label: 'Why do you want to join our company?', placeholder: 'Tell us what interests you...', required: true, multiline: true },
    { id: 'checkbox', name: 'Authorization', type: 'checkbox', label: 'Authorization', required: true, options: ['I authorize background check', 'Information provided is accurate'] }]

  },
  // ─── 8. Product Order Form ────────────────────────────────────────────────
  {
    id: 8,
    title: 'Product Order Form',
    description: 'E-commerce order form with product selection, quantity, shipping, and payment details.',
    category: 'Payment',
    categoryIcon: 'CreditCard',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_16f4e92e5-1764926451552.png',
    thumbnailAlt: 'Product order form with item selection quantity controls shipping address fields and payment method options on clean interface',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1f541a2a7-1769843164304.png',
    previewImageAlt: 'Complete product order template showing product catalog quantity selectors shipping options and payment section',
    usageCount: '1.1k',
    fieldCount: 16,
    sectionCount: 4,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: false,
    fullDescription: 'An e-commerce order form that handles product selection, inventory checking, shipping calculation, and payment processing.',
    includedElements: ['Product Selector', 'Number Input', 'Text Input', 'Dropdown', 'Radio Buttons', 'Card Input'],
    features: [
    'Real-time inventory checking and availability',
    'Dynamic shipping cost calculation based on location',
    'Multiple payment method support including cards and PayPal',
    'Order summary with itemized pricing breakdown',
    'Automated order confirmation and tracking emails'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Product Selection', type: 'content', label: 'Product Selection', content: 'Select Your Products' },
    { id: 'dropdown', name: 'Product', type: 'select', label: 'Product', placeholder: 'Choose product', required: true, options: ['Premium Widget - $99', 'Standard Widget - $49', 'Basic Widget - $29', 'Widget Bundle - $199'] },
    { id: 'number', name: 'Quantity', type: 'input', label: 'Quantity', placeholder: '1', required: true },
    { id: 'dropdown', name: 'Color', type: 'select', label: 'Color', placeholder: 'Select color', required: true, options: ['Black', 'White', 'Blue', 'Red', 'Silver'] },
    { id: 'dropdown', name: 'Size', type: 'select', label: 'Size', placeholder: 'Select size', required: false, options: ['Small', 'Medium', 'Large', 'X-Large'] },
    { id: 'heading', name: 'Customer Information', type: 'content', label: 'Customer Information', content: 'Your Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'heading', name: 'Shipping Address', type: 'content', label: 'Shipping Address', content: 'Shipping Address' },
    { id: 'text', name: 'Address Line 1', type: 'input', label: 'Address Line 1', placeholder: 'Street address', required: true },
    { id: 'text', name: 'City', type: 'input', label: 'City', placeholder: 'City', required: true },
    { id: 'text', name: 'State', type: 'input', label: 'State/Province', placeholder: 'State', required: true },
    { id: 'text', name: 'Postal Code', type: 'input', label: 'Postal Code', placeholder: 'ZIP code', required: true },
    { id: 'dropdown', name: 'Country', type: 'select', label: 'Country', placeholder: 'Select country', required: true, options: ['United States', 'Canada', 'United Kingdom', 'Australia', 'Other'] },
    { id: 'heading', name: 'Shipping Method', type: 'content', label: 'Shipping Method', content: 'Shipping Options' },
    { id: 'radio', name: 'Shipping Speed', type: 'radio', label: 'Shipping Speed', required: true, options: ['Standard (5-7 days) - Free', 'Express (2-3 days) - $15', 'Overnight - $35'] },
    { id: 'text', name: 'Special Instructions', type: 'input', label: 'Delivery Instructions', placeholder: 'Any special delivery instructions?', required: false, multiline: true }]

  },
  // ─── 9. Newsletter Signup ─────────────────────────────────────────────────
  {
    id: 9,
    title: 'Newsletter Signup',
    description: 'Simple newsletter subscription form with email validation and preference selection.',
    category: 'Lead Generation',
    categoryIcon: 'Target',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b9c9c673-1765273095536.png',
    thumbnailAlt: 'Newsletter signup form with email input field subscription preferences checkboxes and subscribe button on gradient background',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_18c9d5f1d-1766620170678.png',
    previewImageAlt: 'Complete newsletter subscription template showing email field content preferences frequency options and privacy policy link',
    usageCount: '3.2k',
    fieldCount: 4,
    sectionCount: 1,
    hasConditionalLogic: false,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A streamlined newsletter subscription form designed for maximum conversion with minimal friction.',
    includedElements: ['Email Input', 'Checkboxes', 'Submit Button'],
    features: [
    'Double opt-in email verification for list quality',
    'Content preference selection for targeted campaigns',
    'Frequency control for subscriber engagement',
    'Clear privacy policy link to support GDPR workflows',
    'Newsletter subscription form layout'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Newsletter Signup', type: 'content', label: 'Newsletter Signup', content: 'Subscribe to Our Newsletter' },
    { id: 'text', name: 'First Name', type: 'input', label: 'First Name', placeholder: 'John', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'your.email@example.com', required: true },
    { id: 'checkbox', name: 'Content Interests', type: 'checkbox', label: 'What content interests you?', required: false, options: ['Product Updates', 'Industry News', 'Tips & Tutorials', 'Special Offers', 'Company News'] },
    { id: 'radio', name: 'Frequency', type: 'radio', label: 'Email Frequency', required: true, options: ['Daily Digest', 'Weekly Summary', 'Monthly Newsletter'] },
    { id: 'checkbox', name: 'Consent', type: 'checkbox', label: 'Privacy & Consent', required: true, options: ['I agree to receive emails and accept the privacy policy'] }]

  },
  // ─── 10. Support Ticket Form ──────────────────────────────────────────────
  {
    id: 10,
    title: 'Support Ticket Form',
    description: 'Technical support form with issue category, priority level, and file attachment.',
    category: 'Contact Forms',
    categoryIcon: 'Mail',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9939139-1785433575500.png",
    thumbnailAlt: 'Support ticket form with issue category dropdown priority selector description textarea and file attachment area on professional interface',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ea7ab22b-1768703999141.png",
    previewImageAlt: 'Complete support ticket template showing category selection priority levels detailed description field and screenshot upload section',
    usageCount: '890',
    fieldCount: 9,
    sectionCount: 2,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A comprehensive support ticket form that helps organize and prioritize customer support requests efficiently.',
    includedElements: ['Dropdown', 'Radio Buttons', 'Textarea', 'File Upload', 'Text Input', 'Email Input'],
    features: [
    'Issue categorization for proper routing to teams',
    'Priority level selection with SLA indicators',
    'Screenshot and log file upload support',
    'Automatic ticket number generation and tracking',
    'Submission dashboard for ticket review and follow-up'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Contact Information', type: 'content', label: 'Contact Information', content: 'Your Contact Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@example.com', required: true },
    { id: 'text', name: 'Account ID', type: 'input', label: 'Account ID or Username', placeholder: 'Your account identifier', required: false },
    { id: 'heading', name: 'Issue Details', type: 'content', label: 'Issue Details', content: 'Describe Your Issue' },
    { id: 'dropdown', name: 'Issue Category', type: 'select', label: 'Issue Category', placeholder: 'Select category', required: true, options: ['Technical Issue', 'Billing Question', 'Feature Request', 'Account Access', 'Bug Report', 'General Inquiry'] },
    { id: 'radio', name: 'Priority', type: 'radio', label: 'Priority Level', required: true, options: ['Low - General question', 'Medium - Issue affecting work', 'High - Critical issue', 'Urgent - ASAP'] },
    { id: 'text', name: 'Subject', type: 'input', label: 'Subject', placeholder: 'Brief description of the issue', required: true },
    { id: 'text', name: 'Description', type: 'input', label: 'Detailed Description', placeholder: 'Please provide as much detail as possible...', required: true, multiline: true },
    { id: 'file', name: 'Attachments', type: 'file', label: 'Attachments', placeholder: 'Upload screenshots or files (optional)', required: false },
    { id: 'text', name: 'Steps to Reproduce', type: 'input', label: 'Steps to Reproduce (if applicable)', placeholder: '1. First I did...\n2. Then I...', required: false, multiline: true }]

  },
  // ─── 11. Quote Request Form ───────────────────────────────────────────────
  {
    id: 11,
    title: 'Quote Request Form',
    description: 'Business quote request with project details, budget range, and timeline requirements.',
    category: 'Lead Generation',
    categoryIcon: 'Target',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1128803eb-1766029958285.png',
    thumbnailAlt: 'Quote request form with project description fields budget range selector timeline picker and company information section',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1863c13b7-1766521013972.png',
    previewImageAlt: 'Complete quote request template showing detailed project requirements budget options timeline calendar and contact details',
    usageCount: '670',
    fieldCount: 13,
    sectionCount: 3,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: true,
    fullDescription: 'A professional quote request form designed to gather all necessary information for accurate project estimation.',
    includedElements: ['Text Input', 'Textarea', 'Dropdown', 'Radio Buttons', 'Date Picker', 'File Upload'],
    features: [
    'Detailed project scope description with examples',
    'Budget range selection with custom options',
    'Timeline requirements with flexible date ranges',
    'File upload for project briefs and specifications',
    'Automated quote generation and email delivery'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Contact Information', type: 'content', label: 'Contact Information', content: 'Your Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@company.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'text', name: 'Company', type: 'input', label: 'Company Name', placeholder: 'Your company', required: true },
    { id: 'heading', name: 'Project Details', type: 'content', label: 'Project Details', content: 'Tell Us About Your Project' },
    { id: 'dropdown', name: 'Service Type', type: 'select', label: 'Service Type', placeholder: 'Select service', required: true, options: ['Web Development', 'Mobile App Development', 'UI/UX Design', 'Digital Marketing', 'Consulting', 'Other'] },
    { id: 'text', name: 'Project Title', type: 'input', label: 'Project Title', placeholder: 'Give your project a name', required: true },
    { id: 'text', name: 'Project Description', type: 'input', label: 'Project Description', placeholder: 'Describe your project in detail...', required: true, multiline: true },
    { id: 'radio', name: 'Budget Range', type: 'radio', label: 'Budget Range', required: true, options: ['Less than $5,000', '$5,000 - $15,000', '$15,000 - $50,000', '$50,000 - $100,000', 'More than $100,000'] },
    { id: 'heading', name: 'Timeline', type: 'content', label: 'Timeline', content: 'Project Timeline' },
    { id: 'date', name: 'Desired Start Date', type: 'input', label: 'Desired Start Date', placeholder: 'Select date', required: true },
    { id: 'dropdown', name: 'Project Duration', type: 'select', label: 'Expected Project Duration', placeholder: 'Select duration', required: true, options: ['Less than 1 month', '1-3 months', '3-6 months', '6-12 months', 'More than 12 months'] },
    { id: 'radio', name: 'Urgency', type: 'radio', label: 'Project Urgency', required: true, options: ['Flexible timeline', 'Moderate urgency', 'High priority', 'Urgent - ASAP'] },
    { id: 'file', name: 'Project Brief', type: 'file', label: 'Project Brief or Requirements', placeholder: 'Upload documents (optional)', required: false },
    { id: 'text', name: 'Additional Notes', type: 'input', label: 'Additional Notes', placeholder: 'Any other information we should know?', required: false, multiline: true }]

  },
  // ─── 12. Webinar Registration ─────────────────────────────────────────────
  {
    id: 12,
    title: 'Webinar Registration',
    description: 'Webinar signup form with session selection, timezone, and reminder preferences.',
    category: 'Registration',
    categoryIcon: 'UserPlus',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1128803eb-1766029958285.png',
    thumbnailAlt: 'Webinar registration form with session time selection timezone dropdown reminder preferences and attendee information fields',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1f5db0c93-1767167973794.png',
    previewImageAlt: 'Complete webinar registration template showing available sessions timezone converter reminder options and calendar integration',
    usageCount: '1.4k',
    fieldCount: 11,
    sectionCount: 3,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A webinar registration form that handles multiple sessions, timezone conversions, and automated reminders.',
    includedElements: ['Text Input', 'Email Input', 'Dropdown', 'Radio Buttons', 'Checkboxes', 'Date Picker'],
    features: [
    'Multiple webinar session selection with capacity limits',
    'Automatic timezone detection and conversion',
    'Customizable reminder preferences via email and SMS',
    'Calendar invite generation with webinar link',
    'Pre-webinar survey for content customization'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Webinar Selection', type: 'content', label: 'Webinar Selection', content: 'Choose Your Session' },
    { id: 'dropdown', name: 'Webinar Session', type: 'select', label: 'Webinar Session', placeholder: 'Select session', required: true, options: ['Session 1: Jan 15, 2026 - 10:00 AM EST', 'Session 2: Jan 15, 2026 - 2:00 PM EST', 'Session 3: Jan 16, 2026 - 10:00 AM EST', 'Session 4: Jan 16, 2026 - 2:00 PM EST'] },
    { id: 'dropdown', name: 'Timezone', type: 'select', label: 'Your Timezone', placeholder: 'Select timezone', required: true, options: ['Eastern Time (ET)', 'Central Time (CT)', 'Mountain Time (MT)', 'Pacific Time (PT)', 'UTC', 'Other'] },
    { id: 'heading', name: 'Your Information', type: 'content', label: 'Your Information', content: 'Attendee Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: false },
    { id: 'text', name: 'Company', type: 'input', label: 'Company/Organization', placeholder: 'Your company', required: false },
    { id: 'dropdown', name: 'Job Title', type: 'select', label: 'Job Title', placeholder: 'Select your role', required: false, options: ['Executive/C-Level', 'Manager', 'Individual Contributor', 'Student', 'Other'] },
    { id: 'heading', name: 'Preferences', type: 'content', label: 'Preferences', content: 'Notification Preferences' },
    { id: 'checkbox', name: 'Reminders', type: 'checkbox', label: 'Send me reminders', required: false, options: ['1 day before', '1 hour before', '15 minutes before'] },
    { id: 'checkbox', name: 'Topics of Interest', type: 'checkbox', label: "Topics you\'re most interested in", required: false, options: ['Product Features', 'Best Practices', 'Case Studies', 'Q&A Session', 'Live Demo'] },
    { id: 'text', name: 'Questions', type: 'input', label: 'Questions for the Speaker', placeholder: "Any questions you\'d like answered during the webinar?", required: false, multiline: true },
    { id: 'checkbox', name: 'Marketing Consent', type: 'checkbox', label: 'Communication', required: false, options: ['Send me information about future webinars'] }]

  },
  // ─── 13. Patient Intake Form ──────────────────────────────────────────────
  {
    id: 13,
    title: 'Patient Intake Form',
    description: 'Medical intake form with personal details, insurance info, and health history.',
    category: 'Contact Forms',
    categoryIcon: 'Mail',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_115e94300-1764652053593.png',
    thumbnailAlt: 'Medical patient intake form with personal information fields insurance details and health history checkboxes on clean white background',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_115e94300-1764652053593.png',
    previewImageAlt: 'Complete patient intake template showing demographics insurance information medical history and consent sections',
    usageCount: '540',
    fieldCount: 20,
    sectionCount: 4,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: true,
    fullDescription: 'A patient intake form for healthcare providers to collect patient demographics, insurance details, and medical history before appointments. Note: verify your plan and agreements meet your regulatory obligations (e.g., HIPAA) before collecting health information.',
    includedElements: ['Text Input', 'Date Picker', 'Dropdown', 'Checkboxes', 'Radio Buttons', 'Signature'],
    features: [
    'Secure data collection with encryption support',
    'Insurance information with provider lookup',
    'Medical history with condition checkboxes',
    'Medication list with dosage fields',
    'Digital consent signature capture'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Patient Information', type: 'content', label: 'Patient Information', content: 'Patient Demographics' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'First and Last Name', required: true },
    { id: 'date', name: 'Date of Birth', type: 'input', label: 'Date of Birth', placeholder: 'MM/DD/YYYY', required: true },
    { id: 'radio', name: 'Gender', type: 'radio', label: 'Gender', required: true, options: ['Male', 'Female', 'Non-binary', 'Prefer not to say'] },
    { id: 'text', name: 'Address', type: 'input', label: 'Home Address', placeholder: 'Street, City, State, ZIP', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'patient@example.com', required: true },
    { id: 'heading', name: 'Insurance', type: 'content', label: 'Insurance', content: 'Insurance Information' },
    { id: 'text', name: 'Insurance Provider', type: 'input', label: 'Insurance Provider', placeholder: 'Provider name', required: true },
    { id: 'text', name: 'Policy Number', type: 'input', label: 'Policy Number', placeholder: 'Policy or member ID', required: true },
    { id: 'text', name: 'Group Number', type: 'input', label: 'Group Number', placeholder: 'Group number (if applicable)', required: false },
    { id: 'heading', name: 'Medical History', type: 'content', label: 'Medical History', content: 'Medical History' },
    { id: 'checkbox', name: 'Conditions', type: 'checkbox', label: 'Do you have any of the following conditions?', required: false, options: ['Diabetes', 'Hypertension', 'Heart Disease', 'Asthma', 'Allergies', 'Cancer (current or past)', 'None of the above'] },
    { id: 'text', name: 'Current Medications', type: 'input', label: 'Current Medications', placeholder: 'List medications and dosages...', required: false, multiline: true },
    { id: 'text', name: 'Allergies', type: 'input', label: 'Known Allergies', placeholder: 'List any known allergies...', required: false, multiline: true },
    { id: 'heading', name: 'Consent', type: 'content', label: 'Consent', content: 'Consent & Authorization' },
    { id: 'checkbox', name: 'Consent', type: 'checkbox', label: 'Patient Consent', required: true, options: ['I consent to treatment', 'I authorize release of medical information', 'I have reviewed the privacy policy'] },
    { id: 'signature', name: 'Signature', type: 'signature', label: 'Patient Signature', required: true }]

  },
  // ─── 14. Volunteer Application ────────────────────────────────────────────
  {
    id: 14,
    title: 'Volunteer Application',
    description: 'Non-profit volunteer sign-up with availability, skills, and background check consent.',
    category: 'Registration',
    categoryIcon: 'UserPlus',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a7c045d3-1768131554866.png',
    thumbnailAlt: 'Volunteer application form with personal details availability calendar skills selection and background check consent on friendly interface',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1da58fe02-1770062248211.png',
    previewImageAlt: 'Complete volunteer application template showing contact information availability preferences skills and authorization sections',
    usageCount: '430',
    fieldCount: 14,
    sectionCount: 3,
    hasConditionalLogic: false,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A volunteer application form for non-profits and community organizations to collect volunteer information, availability, and skills.',
    includedElements: ['Text Input', 'Email Input', 'Checkboxes', 'Dropdown', 'Textarea'],
    features: [
    'Availability schedule with day and time selection',
    'Skills and interests matching for role assignment',
    'Emergency contact information collection',
    'Background check authorization consent',
    'Automated welcome email with onboarding steps'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Personal Information', type: 'content', label: 'Personal Information', content: 'About You' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'text', name: 'Address', type: 'input', label: 'Home Address', placeholder: 'City, State', required: true },
    { id: 'heading', name: 'Availability', type: 'content', label: 'Availability', content: 'Your Availability' },
    { id: 'checkbox', name: 'Days Available', type: 'checkbox', label: 'Days Available', required: true, options: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] },
    { id: 'radio', name: 'Hours Per Week', type: 'radio', label: 'Hours Available Per Week', required: true, options: ['1-5 hours', '5-10 hours', '10-20 hours', '20+ hours'] },
    { id: 'heading', name: 'Skills & Interests', type: 'content', label: 'Skills & Interests', content: 'Skills & Interests' },
    { id: 'checkbox', name: 'Skills', type: 'checkbox', label: 'Relevant Skills', required: false, options: ['Event Planning', 'Fundraising', 'Social Media', 'Teaching/Tutoring', 'Medical/First Aid', 'Construction', 'Administrative', 'Other'] },
    { id: 'dropdown', name: 'Area of Interest', type: 'select', label: 'Primary Area of Interest', placeholder: 'Select area', required: true, options: ['Community Outreach', 'Youth Programs', 'Senior Services', 'Environmental', 'Animal Welfare', 'Disaster Relief', 'Other'] },
    { id: 'text', name: 'Why Volunteer', type: 'input', label: 'Why do you want to volunteer with us?', placeholder: 'Tell us your motivation...', required: true, multiline: true },
    { id: 'text', name: 'Emergency Contact', type: 'input', label: 'Emergency Contact Name & Phone', placeholder: 'Name and phone number', required: true },
    { id: 'checkbox', name: 'Authorization', type: 'checkbox', label: 'Authorization', required: true, options: ['I consent to a background check', 'Information provided is accurate and complete'] }]

  },
  // ─── 15. Employee Satisfaction Survey ────────────────────────────────────
  {
    id: 15,
    title: 'Employee Satisfaction Survey',
    description: 'HR survey measuring employee engagement, work environment, and management effectiveness.',
    category: 'Surveys',
    categoryIcon: 'ClipboardList',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_14b16ce49-1770478805028.png",
    thumbnailAlt: 'Employee satisfaction survey with rating scales work environment questions management feedback and anonymous submission option',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab245ce5-1768741177590.png",
    previewImageAlt: 'Complete employee survey template showing engagement metrics work-life balance questions management ratings and open feedback sections',
    usageCount: '610',
    fieldCount: 16,
    sectionCount: 4,
    hasConditionalLogic: false,
    isFeatured: false,
    isNew: false,
    fullDescription: 'An anonymous employee satisfaction survey to measure engagement, identify pain points, and improve workplace culture.',
    includedElements: ['Radio Buttons', 'Slider', 'Checkboxes', 'Textarea', 'Dropdown'],
    features: [
    'Anonymous submission to encourage honest responses',
    'Engagement score calculation across categories',
    'Department and role segmentation options',
    'Benchmark comparison with industry standards',
    'Automated report generation for HR teams'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Work Environment', type: 'content', label: 'Work Environment', content: 'Work Environment' },
    { id: 'radio', name: 'Overall Satisfaction', type: 'radio', label: 'Overall, how satisfied are you with your job?', required: true, options: ['Very Satisfied', 'Satisfied', 'Neutral', 'Dissatisfied', 'Very Dissatisfied'] },
    { id: 'slider', name: 'Work-Life Balance', type: 'slider', label: 'Rate your work-life balance (1-10)', required: true, min: 1, max: 10 },
    { id: 'radio', name: 'Resources', type: 'radio', label: 'Do you have the tools and resources to do your job effectively?', required: true, options: ['Always', 'Usually', 'Sometimes', 'Rarely', 'Never'] },
    { id: 'heading', name: 'Management', type: 'content', label: 'Management', content: 'Management & Leadership' },
    { id: 'radio', name: 'Manager Support', type: 'radio', label: 'How well does your manager support your professional growth?', required: true, options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
    { id: 'radio', name: 'Communication', type: 'radio', label: 'How would you rate communication from leadership?', required: true, options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
    { id: 'heading', name: 'Team & Culture', type: 'content', label: 'Team & Culture', content: 'Team & Culture' },
    { id: 'radio', name: 'Team Collaboration', type: 'radio', label: 'How well does your team collaborate?', required: true, options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
    { id: 'checkbox', name: 'Culture Values', type: 'checkbox', label: 'Which company values do you feel are lived daily?', required: false, options: ['Integrity', 'Innovation', 'Teamwork', 'Customer Focus', 'Diversity & Inclusion', 'None of the above'] },
    { id: 'heading', name: 'Growth & Development', type: 'content', label: 'Growth & Development', content: 'Growth & Development' },
    { id: 'radio', name: 'Career Growth', type: 'radio', label: 'Are there clear career advancement opportunities?', required: true, options: ['Strongly Agree', 'Agree', 'Neutral', 'Disagree', 'Strongly Disagree'] },
    { id: 'dropdown', name: 'Department', type: 'select', label: 'Your Department (optional)', placeholder: 'Select department', required: false, options: ['Engineering', 'Sales', 'Marketing', 'Operations', 'HR', 'Finance', 'Customer Support', 'Other'] },
    { id: 'text', name: 'Improvements', type: 'input', label: 'What one thing would most improve your work experience?', placeholder: 'Share your thoughts...', required: false, multiline: true },
    { id: 'text', name: 'Additional Feedback', type: 'input', label: 'Any additional feedback?', placeholder: 'Anything else you want to share...', required: false, multiline: true }]

  },
  // ─── 16. Rental Application ───────────────────────────────────────────────
  {
    id: 16,
    title: 'Rental Application',
    description: 'Property rental application with prospective tenant details, employment information, and references.',
    category: 'Contact Forms',
    categoryIcon: 'Mail',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee368a11-1785433575188.png",
    thumbnailAlt: 'Rental application form with tenant personal information employment details rental history and reference fields on professional layout',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_157c8d928-1765365964267.png",
    previewImageAlt: 'Complete rental application template showing applicant details income verification rental history references and authorization sections',
    usageCount: '380',
    fieldCount: 18,
    sectionCount: 4,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A comprehensive rental application form for property managers to collect tenant information, verify employment, and check references.',
    includedElements: ['Text Input', 'Email Input', 'Phone Input', 'Dropdown', 'Radio Buttons', 'Signature'],
    features: [
    'Employment and income information fields',
    'Rental history with previous landlord contacts',
    'Personal and professional reference collection',
    'Pet and vehicle information sections',
    'Digital signature for authorization'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Applicant Information', type: 'content', label: 'Applicant Information', content: 'Applicant Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Legal Name', placeholder: 'First Middle Last', required: true },
    { id: 'date', name: 'Date of Birth', type: 'input', label: 'Date of Birth', placeholder: 'MM/DD/YYYY', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'applicant@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'text', name: 'Current Address', type: 'input', label: 'Current Address', placeholder: 'Street, City, State, ZIP', required: true },
    { id: 'heading', name: 'Employment', type: 'content', label: 'Employment', content: 'Employment & Income' },
    { id: 'text', name: 'Employer', type: 'input', label: 'Current Employer', placeholder: 'Company name', required: true },
    { id: 'text', name: 'Job Title', type: 'input', label: 'Job Title', placeholder: 'Your position', required: true },
    { id: 'number', name: 'Monthly Income', type: 'input', label: 'Monthly Gross Income ($)', placeholder: '0', required: true },
    { id: 'phone', name: 'Employer Phone', type: 'input', label: 'Employer Phone', placeholder: '(555) 123-4567', required: true },
    { id: 'heading', name: 'Rental History', type: 'content', label: 'Rental History', content: 'Rental History' },
    { id: 'text', name: 'Previous Address', type: 'input', label: 'Previous Address', placeholder: 'Street, City, State, ZIP', required: true },
    { id: 'text', name: 'Previous Landlord', type: 'input', label: 'Previous Landlord Name', placeholder: 'Landlord name', required: true },
    { id: 'phone', name: 'Landlord Phone', type: 'input', label: 'Previous Landlord Phone', placeholder: '(555) 123-4567', required: true },
    { id: 'radio', name: 'Pets', type: 'radio', label: 'Do you have pets?', required: true, options: ['No pets', 'Dog(s)', 'Cat(s)', 'Other pets'] },
    { id: 'heading', name: 'Authorization', type: 'content', label: 'Authorization', content: 'Authorization & Consent' },
    { id: 'checkbox', name: 'Consent', type: 'checkbox', label: 'Applicant Authorization', required: true, options: ['I authorize a credit and background check', 'All information provided is accurate and complete'] },
    { id: 'signature', name: 'Signature', type: 'signature', label: 'Applicant Signature', required: true }]

  },
  // ─── 17. Donation Form ────────────────────────────────────────────────────
  {
    id: 17,
    title: 'Donation Form',
    description: 'Charity donation form with amount selection, recurring options, and donor recognition.',
    category: 'Payment',
    categoryIcon: 'CreditCard',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_13e0b9376-1764759999271.png',
    thumbnailAlt: 'Charity donation form with preset amount buttons custom amount field recurring donation toggle and donor dedication section',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1123d8ccb-1766921009739.png',
    previewImageAlt: 'Complete donation form template showing giving amounts recurring options donor information and tribute dedication fields',
    usageCount: '760',
    fieldCount: 10,
    sectionCount: 3,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A donation form for non-profits and charities to collect one-time or recurring donations with donor recognition options.',
    includedElements: ['Radio Buttons', 'Number Input', 'Text Input', 'Email Input', 'Checkbox', 'Dropdown'],
    features: [
    'Preset donation amounts with custom option',
    'Recurring donation setup (monthly, quarterly, annually)',
    'Tribute and memorial dedication fields',
    'Employer matching program information',
    'Automated tax receipt generation and delivery'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Donation Amount', type: 'content', label: 'Donation Amount', content: 'Choose Your Donation Amount' },
    { id: 'radio', name: 'Preset Amount', type: 'radio', label: 'Select Amount', required: true, options: ['$10', '$25', '$50', '$100', '$250', 'Custom Amount'] },
    { id: 'number', name: 'Custom Amount', type: 'input', label: 'Custom Amount ($)', placeholder: 'Enter amount', required: false },
    { id: 'radio', name: 'Frequency', type: 'radio', label: 'Donation Frequency', required: true, options: ['One-time', 'Monthly', 'Quarterly', 'Annually'] },
    { id: 'heading', name: 'Donor Information', type: 'content', label: 'Donor Information', content: 'Your Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: false },
    { id: 'heading', name: 'Dedication', type: 'content', label: 'Dedication', content: 'Tribute or Dedication (Optional)' },
    { id: 'radio', name: 'Dedication Type', type: 'radio', label: 'Dedication Type', required: false, options: ['In honor of', 'In memory of', 'No dedication'] },
    { id: 'text', name: 'Honoree Name', type: 'input', label: 'Honoree Name', placeholder: 'Name of person being honored', required: false },
    { id: 'checkbox', name: 'Anonymous', type: 'checkbox', label: 'Donor Preferences', required: false, options: ['Make my donation anonymous', 'I would like to receive updates about my impact'] }]

  },
  // ─── 18. Conference Room Booking ──────────────────────────────────────────
  {
    id: 18,
    title: 'Conference Room Booking',
    description: 'Internal room reservation form with date, time, attendee count, and equipment needs.',
    category: 'Booking',
    categoryIcon: 'Calendar',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1dd7f81f9-1785433575982.png",
    thumbnailAlt: 'Conference room booking form with room selection calendar date picker time slots attendee count and equipment checklist',
    previewImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1f2e0c14a-1767793718042.png",
    previewImageAlt: 'Complete conference room reservation template showing available rooms time selection capacity options and AV equipment requirements',
    usageCount: '490',
    fieldCount: 10,
    sectionCount: 3,
    hasConditionalLogic: false,
    isFeatured: false,
    isNew: false,
    fullDescription: 'An internal conference room booking form for organizations to manage meeting space reservations and equipment requests.',
    includedElements: ['Dropdown', 'Date Picker', 'Time Picker', 'Number Input', 'Checkboxes', 'Textarea'],
    features: [
    'Real-time room availability checking',
    'Capacity and layout configuration options',
    'AV and catering equipment request checklist',
    'Recurring meeting setup for regular bookings',
    'Calendar integration with automatic invites'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Room Selection', type: 'content', label: 'Room Selection', content: 'Select a Room' },
    { id: 'dropdown', name: 'Room', type: 'select', label: 'Conference Room', placeholder: 'Choose a room', required: true, options: ['Board Room (20 people)', 'Meeting Room A (10 people)', 'Meeting Room B (8 people)', 'Huddle Space (4 people)', 'Training Room (30 people)'] },
    { id: 'date', name: 'Date', type: 'input', label: 'Meeting Date', placeholder: 'Select date', required: true },
    { id: 'time', name: 'Start Time', type: 'input', label: 'Start Time', placeholder: 'Select start time', required: true },
    { id: 'time', name: 'End Time', type: 'input', label: 'End Time', placeholder: 'Select end time', required: true },
    { id: 'number', name: 'Attendees', type: 'input', label: 'Number of Attendees', placeholder: '1', required: true },
    { id: 'heading', name: 'Equipment', type: 'content', label: 'Equipment', content: 'Equipment & Services' },
    { id: 'checkbox', name: 'Equipment Needed', type: 'checkbox', label: 'Equipment Needed', required: false, options: ['Projector/Screen', 'Video Conferencing', 'Whiteboard', 'Flip Chart', 'Microphone', 'Catering Setup'] },
    { id: 'heading', name: 'Meeting Details', type: 'content', label: 'Meeting Details', content: 'Meeting Details' },
    { id: 'text', name: 'Meeting Title', type: 'input', label: 'Meeting Title', placeholder: 'Purpose of the meeting', required: true },
    { id: 'text', name: 'Organizer', type: 'input', label: 'Organizer Name', placeholder: 'Your name', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Organizer Email', placeholder: 'your@company.com', required: true },
    { id: 'text', name: 'Special Requirements', type: 'input', label: 'Special Requirements', placeholder: 'Any special setup or requirements?', required: false, multiline: true }]

  },
  // ─── 19. Product Feedback Form ────────────────────────────────────────────
  {
    id: 19,
    title: 'Product Feedback Form',
    description: 'Post-purchase product review form with star ratings, feature feedback, and improvement suggestions.',
    category: 'Surveys',
    categoryIcon: 'ClipboardList',
    thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1e6f44f12-1769247800684.png",
    thumbnailAlt: 'Product feedback form with star rating system feature evaluation checkboxes improvement suggestions textarea and recommendation question',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1e6f44f12-1769247800684.png',
    previewImageAlt: 'Complete product review template showing purchase details rating scales feature feedback and open-ended improvement suggestions',
    usageCount: '820',
    fieldCount: 12,
    sectionCount: 3,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: true,
    fullDescription: 'A post-purchase product feedback form to collect detailed reviews, identify improvement areas, and gather testimonials.',
    includedElements: ['Radio Buttons', 'Slider', 'Checkboxes', 'Textarea', 'Dropdown'],
    features: [
    'Star rating system for overall product score',
    'Feature-by-feature evaluation matrix',
    'Photo and video review upload support',
    'Net Promoter Score (NPS) question included',
    'Testimonial consent for marketing use'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Product Rating', type: 'content', label: 'Product Rating', content: 'Rate Your Purchase' },
    { id: 'dropdown', name: 'Product', type: 'select', label: 'Which product are you reviewing?', placeholder: 'Select product', required: true, options: ['Product A', 'Product B', 'Product C', 'Product D', 'Other'] },
    { id: 'radio', name: 'Overall Rating', type: 'radio', label: 'Overall Rating', required: true, options: ['⭐⭐⭐⭐⭐ Excellent', '⭐⭐⭐⭐ Good', '⭐⭐⭐ Average', '⭐⭐ Poor', '⭐ Very Poor'] },
    { id: 'slider', name: 'Value for Money', type: 'slider', label: 'Value for Money (1-10)', required: true, min: 1, max: 10 },
    { id: 'heading', name: 'Feature Feedback', type: 'content', label: 'Feature Feedback', content: 'Feature Evaluation' },
    { id: 'checkbox', name: 'Liked Features', type: 'checkbox', label: 'What did you like most?', required: false, options: ['Quality', 'Design', 'Ease of Use', 'Performance', 'Durability', 'Price', 'Packaging'] },
    { id: 'checkbox', name: 'Disliked Features', type: 'checkbox', label: 'What could be improved?', required: false, options: ['Quality', 'Design', 'Ease of Use', 'Performance', 'Durability', 'Price', 'Packaging'] },
    { id: 'heading', name: 'Your Review', type: 'content', label: 'Your Review', content: 'Write Your Review' },
    { id: 'text', name: 'Review Title', type: 'input', label: 'Review Title', placeholder: 'Summarize your experience in one line', required: true },
    { id: 'text', name: 'Review Body', type: 'input', label: 'Detailed Review', placeholder: 'Tell others about your experience with this product...', required: true, multiline: true },
    { id: 'radio', name: 'Recommend', type: 'radio', label: 'Would you recommend this product?', required: true, options: ['Definitely yes', 'Probably yes', 'Not sure', 'Probably not', 'Definitely not'] },
    { id: 'checkbox', name: 'Consent', type: 'checkbox', label: 'Review Consent', required: false, options: ['I consent to this review being published on the website'] }]

  },
  // ─── 20. Scholarship Application ─────────────────────────────────────────
  {
    id: 20,
    title: 'Scholarship Application',
    description: 'Academic scholarship form with GPA, essay questions, financial need, and recommendation letters.',
    category: 'Registration',
    categoryIcon: 'UserPlus',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_11fefffe5-1765273095596.png',
    thumbnailAlt: 'Scholarship application form with student information academic achievements essay questions financial need section and document upload',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_11fefffe5-1765273095596.png',
    previewImageAlt: 'Complete scholarship application template showing personal details academic history essay prompts financial information and reference uploads',
    usageCount: '310',
    fieldCount: 20,
    sectionCount: 5,
    hasConditionalLogic: false,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A scholarship application form for educational institutions to collect student information, academic achievements, and essays.',
    includedElements: ['Text Input', 'Email Input', 'Dropdown', 'Textarea', 'File Upload', 'Number Input'],
    features: [
    'Academic achievement and GPA collection',
    'Essay prompts with word count guidance',
    'Financial need documentation upload',
    'Extracurricular activities and awards section',
    'Recommendation letter request integration'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Personal Information', type: 'content', label: 'Personal Information', content: 'Applicant Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Legal Name', placeholder: 'First Middle Last', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'student@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'date', name: 'Date of Birth', type: 'input', label: 'Date of Birth', placeholder: 'MM/DD/YYYY', required: true },
    { id: 'heading', name: 'Academic Information', type: 'content', label: 'Academic Information', content: 'Academic Background' },
    { id: 'text', name: 'School', type: 'input', label: 'Current School/University', placeholder: 'Institution name', required: true },
    { id: 'dropdown', name: 'Year', type: 'select', label: 'Current Year', placeholder: 'Select year', required: true, options: ['Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate Student'] },
    { id: 'text', name: 'Major', type: 'input', label: 'Major/Field of Study', placeholder: 'Your major', required: true },
    { id: 'number', name: 'GPA', type: 'input', label: 'Current GPA (out of 4.0)', placeholder: '0.00', required: true },
    { id: 'heading', name: 'Essays', type: 'content', label: 'Essays', content: 'Essay Questions' },
    { id: 'text', name: 'Essay 1', type: 'input', label: 'Describe your academic and career goals (500 words max)', placeholder: 'Share your goals and aspirations...', required: true, multiline: true },
    { id: 'text', name: 'Essay 2', type: 'input', label: 'Describe a challenge you have overcome (300 words max)', placeholder: 'Tell us about a challenge and what you learned...', required: true, multiline: true },
    { id: 'heading', name: 'Activities', type: 'content', label: 'Activities', content: 'Extracurricular Activities' },
    { id: 'text', name: 'Activities', type: 'input', label: 'List your extracurricular activities and leadership roles', placeholder: 'Activities, clubs, sports, volunteer work...', required: true, multiline: true },
    { id: 'text', name: 'Awards', type: 'input', label: 'Awards and Honors', placeholder: 'List any awards or recognitions...', required: false, multiline: true },
    { id: 'heading', name: 'Documents', type: 'content', label: 'Documents', content: 'Required Documents' },
    { id: 'file', name: 'Transcript', type: 'file', label: 'Official Transcript', placeholder: 'Upload your transcript', required: true },
    { id: 'file', name: 'Financial Aid', type: 'file', label: 'Financial Need Documentation', placeholder: 'Upload financial documents (optional)', required: false },
    { id: 'checkbox', name: 'Certification', type: 'checkbox', label: 'Certification', required: true, options: ['I certify that all information provided is accurate and complete'] }]

  },
  // ─── 21. Gym Membership Form ──────────────────────────────────────────────
  {
    id: 21,
    title: 'Gym Membership Form',
    description: 'Fitness center membership sign-up with plan selection, health screening, and payment.',
    category: 'Booking',
    categoryIcon: 'Calendar',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_13369dc1f-1764674756336.png',
    thumbnailAlt: 'Gym membership form with plan selection health screening questions fitness goals and payment information on energetic design',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_119e97723-1767075064687.png',
    previewImageAlt: 'Complete gym membership template showing membership tiers health questionnaire fitness goals emergency contact and billing details',
    usageCount: '560',
    fieldCount: 14,
    sectionCount: 4,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A gym membership registration form that collects member information, health screening data, fitness goals, and payment details.',
    includedElements: ['Radio Buttons', 'Checkboxes', 'Text Input', 'Email Input', 'Dropdown', 'Signature'],
    features: [
    'Membership plan comparison and selection',
    'Health screening questionnaire for safety',
    'Fitness goals and experience level assessment',
    'Emergency contact information collection',
    'Digital waiver and liability release signature'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Membership Plan', type: 'content', label: 'Membership Plan', content: 'Choose Your Membership' },
    { id: 'radio', name: 'Plan', type: 'radio', label: 'Membership Plan', required: true, options: ['Basic - $29/month', 'Standard - $49/month', 'Premium - $79/month', 'Annual - $499/year'] },
    { id: 'heading', name: 'Personal Information', type: 'content', label: 'Personal Information', content: 'Your Information' },
    { id: 'text', name: 'Full Name', type: 'input', label: 'Full Name', placeholder: 'John Smith', required: true },
    { id: 'date', name: 'Date of Birth', type: 'input', label: 'Date of Birth', placeholder: 'MM/DD/YYYY', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Email Address', placeholder: 'john@example.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'text', name: 'Emergency Contact', type: 'input', label: 'Emergency Contact Name & Phone', placeholder: 'Name and phone number', required: true },
    { id: 'heading', name: 'Health Screening', type: 'content', label: 'Health Screening', content: 'Health Screening' },
    { id: 'radio', name: 'Doctor Clearance', type: 'radio', label: 'Have you been cleared by a doctor for physical activity?', required: true, options: ['Yes', 'No', 'Not required'] },
    { id: 'checkbox', name: 'Health Conditions', type: 'checkbox', label: 'Do you have any of the following?', required: false, options: ['Heart condition', 'High blood pressure', 'Diabetes', 'Joint problems', 'Pregnancy', 'None of the above'] },
    { id: 'heading', name: 'Fitness Goals', type: 'content', label: 'Fitness Goals', content: 'Your Fitness Goals' },
    { id: 'checkbox', name: 'Goals', type: 'checkbox', label: 'Primary Fitness Goals', required: true, options: ['Weight Loss', 'Muscle Building', 'Cardio Fitness', 'Flexibility', 'Sports Performance', 'General Health'] },
    { id: 'dropdown', name: 'Experience Level', type: 'select', label: 'Fitness Experience Level', placeholder: 'Select level', required: true, options: ['Beginner', 'Intermediate', 'Advanced', 'Professional Athlete'] },
    { id: 'checkbox', name: 'Waiver', type: 'checkbox', label: 'Liability Waiver', required: true, options: ['I have read and agree to the membership terms and liability waiver'] },
    { id: 'signature', name: 'Signature', type: 'signature', label: 'Member Signature', required: true }]

  },
  // ─── 22. Market Research Survey ───────────────────────────────────────────
  {
    id: 22,
    title: 'Market Research Survey',
    description: 'Consumer insights survey with demographics, purchasing behavior, and brand perception.',
    category: 'Surveys',
    categoryIcon: 'ClipboardList',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_108b7f753-1764643792804.png',
    thumbnailAlt: 'Market research survey with demographic questions purchasing behavior scales brand awareness checkboxes and open-ended feedback',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1e6f44f12-1769247800684.png',
    previewImageAlt: 'Complete market research template showing consumer demographics buying habits brand perception ratings and competitive analysis questions',
    usageCount: '730',
    fieldCount: 14,
    sectionCount: 3,
    hasConditionalLogic: true,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A market research survey to gather consumer insights on demographics, purchasing behavior, and brand perception for strategic decision-making.',
    includedElements: ['Radio Buttons', 'Checkboxes', 'Dropdown', 'Slider', 'Textarea'],
    features: [
    'Demographic profiling with age, income, and location',
    'Purchasing behavior and frequency analysis',
    'Brand awareness and perception measurement',
    'Competitive landscape and preference mapping',
    'Open-ended insights for qualitative analysis'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Demographics', type: 'content', label: 'Demographics', content: 'About You' },
    { id: 'radio', name: 'Age Group', type: 'radio', label: 'Age Group', required: true, options: ['Under 18', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'] },
    { id: 'radio', name: 'Gender', type: 'radio', label: 'Gender', required: false, options: ['Male', 'Female', 'Non-binary', 'Prefer not to say'] },
    { id: 'dropdown', name: 'Income', type: 'select', label: 'Annual Household Income', placeholder: 'Select range', required: false, options: ['Under $25,000', '$25,000-$50,000', '$50,000-$75,000', '$75,000-$100,000', '$100,000-$150,000', 'Over $150,000', 'Prefer not to say'] },
    { id: 'dropdown', name: 'Location', type: 'select', label: 'Region', placeholder: 'Select region', required: true, options: ['Northeast', 'Southeast', 'Midwest', 'Southwest', 'West', 'Outside US'] },
    { id: 'heading', name: 'Purchasing Behavior', type: 'content', label: 'Purchasing Behavior', content: 'Purchasing Habits' },
    { id: 'radio', name: 'Purchase Frequency', type: 'radio', label: 'How often do you purchase products in this category?', required: true, options: ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Rarely', 'Never'] },
    { id: 'checkbox', name: 'Purchase Channels', type: 'checkbox', label: 'Where do you typically shop?', required: true, options: ['Online (website)', 'Mobile app', 'Physical store', 'Social media', 'Subscription service'] },
    { id: 'slider', name: 'Price Sensitivity', type: 'slider', label: 'How price-sensitive are you? (1=Not at all, 10=Very sensitive)', required: true, min: 1, max: 10 },
    { id: 'heading', name: 'Brand Perception', type: 'content', label: 'Brand Perception', content: 'Brand Awareness' },
    { id: 'checkbox', name: 'Brands Known', type: 'checkbox', label: 'Which brands are you aware of?', required: false, options: ['Brand A', 'Brand B', 'Brand C', 'Brand D', 'Brand E', 'None of the above'] },
    { id: 'radio', name: 'Brand Used', type: 'radio', label: 'Which brand do you currently use most?', required: false, options: ['Brand A', 'Brand B', 'Brand C', 'Brand D', 'Brand E', 'None'] },
    { id: 'checkbox', name: 'Decision Factors', type: 'checkbox', label: 'What factors most influence your purchase decision?', required: true, options: ['Price', 'Quality', 'Brand reputation', 'Reviews', 'Convenience', 'Sustainability', 'Recommendations'] },
    { id: 'text', name: 'Additional Insights', type: 'input', label: 'Any additional thoughts or feedback?', placeholder: 'Share any other insights...', required: false, multiline: true }]

  },
  // ─── 23. Partnership Inquiry Form ────────────────────────────────────────
  {
    id: 23,
    title: 'Partnership Inquiry Form',
    description: 'Business partnership request with company overview, collaboration type, and proposal details.',
    category: 'Lead Generation',
    categoryIcon: 'Target',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b8e7aa69-1764728384722.png',
    thumbnailAlt: 'Partnership inquiry form with company information fields collaboration type selection proposal description and contact details',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1058f5068-1764743096096.png',
    previewImageAlt: 'Complete partnership inquiry template showing business overview partnership type goals mutual benefits and next steps sections',
    usageCount: '290',
    fieldCount: 12,
    sectionCount: 3,
    hasConditionalLogic: false,
    isFeatured: false,
    isNew: true,
    fullDescription: 'A business partnership inquiry form to collect information from potential partners, evaluate fit, and initiate collaboration discussions.',
    includedElements: ['Text Input', 'Email Input', 'Dropdown', 'Checkboxes', 'Textarea', 'URL Input'],
    features: [
    'Company overview and industry classification',
    'Partnership type and collaboration goals',
    'Mutual benefit description and value proposition',
    'Existing customer base and market reach data',
    'Automated routing to business development team'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Company Information', type: 'content', label: 'Company Information', content: 'About Your Company' },
    { id: 'text', name: 'Company Name', type: 'input', label: 'Company Name', placeholder: 'Your company name', required: true },
    { id: 'url', name: 'Website', type: 'input', label: 'Company Website', placeholder: 'https://yourcompany.com', required: true },
    { id: 'dropdown', name: 'Industry', type: 'select', label: 'Industry', placeholder: 'Select industry', required: true, options: ['Technology', 'Healthcare', 'Finance', 'Retail', 'Education', 'Media', 'Manufacturing', 'Other'] },
    { id: 'text', name: 'Company Size', type: 'input', label: 'Company Size (employees)', placeholder: 'e.g., 50-200', required: true },
    { id: 'heading', name: 'Contact Person', type: 'content', label: 'Contact Person', content: 'Primary Contact' },
    { id: 'text', name: 'Contact Name', type: 'input', label: 'Contact Name', placeholder: 'Full name', required: true },
    { id: 'text', name: 'Title', type: 'input', label: 'Job Title', placeholder: 'Your title', required: true },
    { id: 'email', name: 'Email', type: 'input', label: 'Business Email', placeholder: 'contact@company.com', required: true },
    { id: 'phone', name: 'Phone', type: 'input', label: 'Phone Number', placeholder: '(555) 123-4567', required: true },
    { id: 'heading', name: 'Partnership Details', type: 'content', label: 'Partnership Details', content: 'Partnership Proposal' },
    { id: 'checkbox', name: 'Partnership Type', type: 'checkbox', label: 'Type of Partnership', required: true, options: ['Technology Integration', 'Reseller/Referral', 'Co-marketing', 'Strategic Alliance', 'White Label', 'Joint Venture'] },
    { id: 'text', name: 'Proposal', type: 'input', label: 'Describe your partnership proposal', placeholder: 'What kind of collaboration are you envisioning and what value would it bring to both parties?', required: true, multiline: true },
    { id: 'text', name: 'Why Us', type: 'input', label: 'Why do you want to partner with us specifically?', placeholder: 'Tell us what attracted you to our company...', required: true, multiline: true }]

  },
  // ─── 24. Bug Report Form ──────────────────────────────────────────────────
  {
    id: 24,
    title: 'Bug Report Form',
    description: 'Developer-friendly bug report with reproduction steps, environment details, and severity.',
    category: 'Contact Forms',
    categoryIcon: 'Mail',
    thumbnail: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a7b71218-1770062248105.png',
    thumbnailAlt: 'Bug report form with issue title severity selector browser environment fields reproduction steps and screenshot upload on technical interface',
    previewImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a54ce279-1766179833409.png',
    previewImageAlt: 'Complete bug report template showing issue description severity classification environment details steps to reproduce and expected versus actual behavior',
    usageCount: '450',
    fieldCount: 13,
    sectionCount: 3,
    hasConditionalLogic: false,
    isFeatured: false,
    isNew: false,
    fullDescription: 'A structured bug report form for development teams to collect detailed issue information, reproduction steps, and environment details.',
    includedElements: ['Text Input', 'Dropdown', 'Radio Buttons', 'Textarea', 'File Upload', 'Checkboxes'],
    features: [
    'Severity and priority classification system',
    'Browser and OS environment detection fields',
    'Step-by-step reproduction instructions',
    'Expected vs actual behavior comparison',
    'Screenshot and log file attachment support'],

    formElements: [
    APP_THEME_HEADER,
    { id: 'heading', name: 'Bug Details', type: 'content', label: 'Bug Details', content: 'Bug Report Details' },
    { id: 'text', name: 'Bug Title', type: 'input', label: 'Bug Title', placeholder: 'Brief description of the issue', required: true },
    { id: 'radio', name: 'Severity', type: 'radio', label: 'Severity', required: true, options: ['Critical - App crashes or data loss', 'High - Major feature broken', 'Medium - Feature partially broken', 'Low - Minor visual or UX issue'] },
    { id: 'dropdown', name: 'Component', type: 'select', label: 'Affected Component', placeholder: 'Select component', required: true, options: ['Form Builder', 'Dashboard', 'Authentication', 'Payments', 'Analytics', 'Integrations', 'Other'] },
    { id: 'heading', name: 'Environment', type: 'content', label: 'Environment', content: 'Environment Details' },
    { id: 'dropdown', name: 'Browser', type: 'select', label: 'Browser', placeholder: 'Select browser', required: true, options: ['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera', 'Other'] },
    { id: 'dropdown', name: 'OS', type: 'select', label: 'Operating System', placeholder: 'Select OS', required: true, options: ['Windows 11', 'Windows 10', 'macOS', 'Linux', 'iOS', 'Android'] },
    { id: 'text', name: 'App Version', type: 'input', label: 'App Version', placeholder: 'e.g., v2.1.0', required: false },
    { id: 'heading', name: 'Reproduction', type: 'content', label: 'Reproduction', content: 'How to Reproduce' },
    { id: 'text', name: 'Steps', type: 'input', label: 'Steps to Reproduce', placeholder: '1. Go to...\n2. Click on...\n3. Observe that...', required: true, multiline: true },
    { id: 'text', name: 'Expected Behavior', type: 'input', label: 'Expected Behavior', placeholder: 'What should happen?', required: true, multiline: true },
    { id: 'text', name: 'Actual Behavior', type: 'input', label: 'Actual Behavior', placeholder: 'What actually happens?', required: true, multiline: true },
    { id: 'file', name: 'Screenshots', type: 'file', label: 'Screenshots or Screen Recording', placeholder: 'Upload evidence (optional)', required: false },
    { id: 'text', name: 'Additional Context', type: 'input', label: 'Additional Context', placeholder: 'Any other relevant information...', required: false, multiline: true }]

  }];

  const catalogTemplates = allTemplates?.map((template) => ({
    ...template,
    ...getTemplatePageById(template?.id),
  }));


  const filteredAndSortedTemplates = useMemo(() => {
    let filtered = catalogTemplates;

    if (activeCategory !== 'all') {
      const categoryLabel = categories?.find((cat) => cat?.id === activeCategory)?.label;
      filtered = filtered?.filter((template) => template?.category === categoryLabel);
    }

    if (searchQuery) {
      const query = searchQuery?.toLowerCase();
      filtered = filtered?.filter(
        (template) =>
        template?.title?.toLowerCase()?.includes(query) ||
        template?.description?.toLowerCase()?.includes(query) ||
        template?.category?.toLowerCase()?.includes(query)
      );
    }

    const sorted = [...filtered]?.sort((a, b) => {
      switch (sortBy) {
        case 'popular':
          return parseFloat(b?.usageCount) - parseFloat(a?.usageCount);
        case 'recent':
          return b?.isNew ? 1 : -1;
        case 'alphabetical':
          return a?.title?.localeCompare(b?.title);
        case 'usage':
          return parseFloat(b?.usageCount) - parseFloat(a?.usageCount);
        default:
          return 0;
      }
    });

    return sorted;
  }, [activeCategory, searchQuery, sortBy]);

  const featuredTemplates = catalogTemplates?.filter((template) => template?.isFeatured);

  const handleUseTemplate = (template) => {
    if (!user) {
      navigate('/signup?next=%2Fform-builder');
      return;
    }
    if (!canCreateForm()) {
      setShowUpgradeLimitModal(true);
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      navigate('/form-builder', { state: { template } });
    }, 800);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const renderWithShell = (content) => (
    user
      ? <NavigationBar>{content}</NavigationBar>
      : <div className="min-h-screen bg-background"><HomeHeader />{content}</div>
  );

  const detailPage = templateSlug ? getTemplatePageBySlug(templateSlug) : null;
  const detailTemplate = detailPage
    ? catalogTemplates?.find((template) => template?.id === detailPage?.id)
    : null;

  if (isLoading) {
    return <LoadingStateManager type="spinner" size="lg" message="Loading template into builder..." fullScreen />;
  }

  if (templateSlug && !detailTemplate) {
    return renderWithShell(
      <main className={`${user ? 'py-10' : 'pt-28 pb-12'} px-4 sm:px-6`}>
        <Helmet>
          <title>Template Not Found | WebForms.biz</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <div className="max-w-2xl mx-auto text-center rounded-2xl border border-border bg-card p-8 sm:p-12">
          <div className="w-14 h-14 mx-auto mb-5 rounded-full bg-muted flex items-center justify-center">
            <Icon name="FileQuestion" size={28} color="var(--color-muted-foreground)" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-foreground mb-3">Template not found</h1>
          <p className="text-muted-foreground mb-7">
            This template may have moved or the address may be incorrect. Browse the gallery to find another starting point.
          </p>
          <Link to="/templates-gallery" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors">
            <Icon name="ArrowLeft" size={17} />
            Browse all templates
          </Link>
        </div>
      </main>
    );
  }

  if (detailTemplate) {
    const canonicalUrl = `https://webforms.biz/templates/${detailTemplate?.slug}`;
    const { title: pageTitle, description: pageDescription } = getTemplatePageMetadata(detailTemplate);
    return renderWithShell(
      <>
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <link rel="canonical" href={canonicalUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:site_name" content="WebForms.biz" />
          <meta property="og:image" content={detailTemplate?.previewImage} />
          <meta property="og:image:alt" content={`${detailTemplate?.title} form template preview`} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content={detailTemplate?.previewImage} />
          <meta name="twitter:image:alt" content={`${detailTemplate?.title} form template preview`} />
        </Helmet>
        <main className={`${user ? 'py-8' : 'pt-24 pb-12'} px-4 sm:px-6 lg:px-8`}>
          <div className="max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li><Icon name="ChevronRight" size={14} /></li>
                <li><Link to="/templates-gallery" className="hover:text-primary">Templates</Link></li>
                <li><Icon name="ChevronRight" size={14} /></li>
                <li className="text-foreground font-medium">{detailTemplate?.title}</li>
              </ol>
            </nav>

            <section aria-labelledby="template-form-name" className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-8 items-start">
              <div className="space-y-8 min-w-0">
                <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    <h2 className="font-semibold text-foreground">Interactive form preview</h2>
                    <span className="hidden sm:inline text-xs text-muted-foreground">Try filling it out</span>
                  </div>
                  <FormPreviewRenderer template={detailTemplate} />
                </div>

                <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
                  <h2 className="text-xl sm:text-2xl font-heading font-semibold text-foreground mb-2">
                    Who this {detailTemplate?.title?.toLowerCase()} is for
                  </h2>
                  <p className="text-muted-foreground mb-5">
                    Designed for {detailTemplate?.audience?.toLowerCase()} who need a practical starting point they can customize to match their workflow.
                  </p>
                  <h3 className="font-semibold text-foreground mb-3">Common uses</h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {detailTemplate?.useCases?.map((useCase) => (
                      <li key={useCase} className="flex items-center gap-2 rounded-lg bg-primary/5 border border-primary/10 p-3 text-sm text-foreground">
                        <Icon name="Check" size={16} className="text-primary flex-shrink-0" />
                        {useCase}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
                  <h2 className="text-xl sm:text-2xl font-heading font-semibold text-foreground mb-5">What’s included</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-7">
                    {detailTemplate?.features?.map((feature) => (
                      <div key={feature} className="flex items-start gap-3 rounded-lg bg-muted/50 p-3">
                        <Icon name="CheckCircle2" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <h3 className="font-semibold text-foreground mb-3">Form elements</h3>
                  <div className="flex flex-wrap gap-2">
                    {detailTemplate?.includedElements?.map((element) => (
                      <span key={element} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">{element}</span>
                    ))}
                  </div>
                </div>
              </div>

              <aside className="xl:sticky xl:top-24 space-y-5">
                <div className="rounded-2xl border border-border bg-card p-6 sm:p-7 shadow-sm">
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                      <Icon name={detailTemplate?.categoryIcon} size={13} />
                      {detailTemplate?.category}
                    </span>
                    {detailTemplate?.isFeatured && <span className="px-2.5 py-1 rounded-full bg-accent/15 text-accent-foreground text-xs font-medium">Featured</span>}
                  </div>
                  <h1 id="template-form-name" className="text-3xl sm:text-4xl font-heading font-semibold text-foreground mb-4">
                    {detailTemplate.title}
                  </h1>
                  <p className="text-muted-foreground leading-relaxed mb-4">{detailTemplate?.description}</p>
                  <p className="text-sm text-foreground leading-relaxed mb-6">{detailTemplate?.fullDescription}</p>

                  <Button variant="default" fullWidth iconName="Plus" iconPosition="left" onClick={() => handleUseTemplate(detailTemplate)}>
                    Use This Template
                  </Button>
                  {!user && <p className="text-xs text-center text-muted-foreground mt-3">Free account required to customize and publish.</p>}

                  <dl className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-border">
                    {[
                      ['Fields', detailTemplate?.fieldCount],
                      ['Sections', detailTemplate?.sectionCount],
                      ['Conditional logic', detailTemplate?.hasConditionalLogic ? 'Included' : 'Not included'],
                      ['Responsive', 'Mobile optimized'],
                      ['Uses', detailTemplate?.usageCount],
                      ['Access', 'Free to preview'],
                    ]?.map(([label, value]) => (
                      <div key={label} className="rounded-lg bg-muted/50 p-3">
                        <dt className="text-xs text-muted-foreground mb-1">{label}</dt>
                        <dd className="text-sm font-medium text-foreground">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </aside>
            </section>
          </div>
        </main>
        <UpgradeLimitModal
          open={showUpgradeLimitModal}
          onClose={() => setShowUpgradeLimitModal(false)}
          onUpgrade={() => navigate('/account-settings?tab=billing')}
          limits={limits}
          planType={creatorPlanType}
        />
      </>
    );
  }

  return renderWithShell(
    <>
      <Helmet>
        <title>Form Templates Gallery | WebForms.biz</title>
        <meta
          name="description"
          content="Browse professionally designed WebForms.biz templates for contact forms, lead capture, surveys, bookings, payments, and more."
        />
        <link rel="canonical" href="https://webforms.biz/templates-gallery" />
        <meta property="og:title" content="Form Templates Gallery | WebForms.biz" />
        <meta property="og:description" content="Browse professionally designed WebForms.biz templates for contact forms, lead capture, surveys, bookings, payments, and more." />
        <meta property="og:url" content="https://webforms.biz/templates-gallery" />
      </Helmet>
      <main className={user ? '' : 'pt-20'}>
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-6 md:mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                <Icon name="LayoutTemplate" size={24} color="var(--color-primary)" />
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
                Template Gallery
              </h1>
            </div>
            <p className="text-sm md:text-base text-muted-foreground">
              {catalogTemplates?.length} professionally designed templates — explore details, preview each form, or open one in the Form Builder
            </p>
          </div>

          <div className="mb-6 md:mb-8">
            <SearchAndSort
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              sortBy={sortBy}
              onSortChange={setSortBy} />
            
          </div>

          <div className="mb-6 md:mb-8 overflow-x-auto scrollbar-custom">
            <CategoryFilter
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory} />
            
          </div>

          {!searchQuery && activeCategory === 'all' &&
          <FeaturedTemplates
            templates={featuredTemplates}
            onUseTemplate={handleUseTemplate} />

          }

          <div className="mb-4 md:mb-6">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground mb-1">
              {activeCategory === 'all' ? 'All Templates' :
              categories?.find((cat) => cat?.id === activeCategory)?.label}
            </h2>
            <p className="text-sm text-muted-foreground">
              {filteredAndSortedTemplates?.length} template
              {filteredAndSortedTemplates?.length !== 1 ? 's' : ''} available
            </p>
          </div>

          {filteredAndSortedTemplates?.length > 0 ?
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filteredAndSortedTemplates?.map((template) =>
            <TemplateCard
              key={template?.id}
              template={template}
              onUseTemplate={handleUseTemplate} />

            )}
            </div> :

          <EmptyState searchQuery={searchQuery} onClearSearch={handleClearSearch} />
          }
        </div>

        <UpgradeLimitModal
          open={showUpgradeLimitModal}
          onClose={() => setShowUpgradeLimitModal(false)}
          onUpgrade={() => navigate('/account-settings?tab=billing')}
          limits={limits}
          planType={creatorPlanType}
        />
      </main>
    </>
  );

};

export default TemplatesGallery;